import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  CardHeader,
  Table,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Col,
  Spinner,
} from "reactstrap";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { FaWallet, FaEnvelope, FaPlusCircle } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Userlist = () => {
  // State variables
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReferral, setSelectedReferral] = useState({});
  const [tokenModal, setTokenModal] = useState(false);
  const [viewAllModal, setViewAllModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState({
    _id: "",
    name: "",
    email: "",
    phoneNumber: "",
    country_prefix: "",
    password_hash: "",
    role: "",
    wallet: "",
    two_way_auth: false,
    user_image: "",
    tokens: 0,
    profile_completed: false,
    notification_signup: false,
    city: "",
    country: "",
    isblocked: false,
    kicked: false,
    totalDeposit: 0,
    totalWagr: 0,
    totalReferralFriends: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filterMode, setFilterMode] = useState("");
  const [userList, setUserList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    country_prefix: "",
    password: null,
    role: "user",
    wallet: "",
    two_way_auth: false,
    profile_completed: false,
    notification_signup: false,
    referral_code: "",
  });
  // State for pagination from backend
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  // Ref for search timeout
  const searchTimeoutRef = useRef(null);
  // Ref to prevent duplicate requests
  const isFetchingRef = useRef(false);

  // ============================================================
  // FIX 1: authFetch with CORRECT error handling (no Axios dependency)
  // ============================================================
  const authFetch = async (url, options = {}) => {
    const token = localStorage.getItem("token");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...options.headers,
    };

    const config = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${url}`,
        config,
      );

      // ============================================================
      // FIX: Proper auth redirect - check response.status directly
      // ============================================================
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem("token");
        // Use window.location for immediate redirect
        window.location.href = "/login";
        throw new Error("Authentication failed - redirecting to login");
      }

      // Handle 409 conflicts specially (they have response body)
      if (response.status === 409) {
        const errorData = await response.json().catch(() => ({}));
        throw { 
          status: response.status, 
          data: errorData, 
          message: errorData.message || "Conflict error" 
        };
      }

      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If can't parse JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return { data, status: response.status };
    } catch (error) {
      // If it's an auth error with redirect, re-throw
      if (error.message?.includes("Authentication failed") || 
          error.message?.includes("redirecting to login")) {
        throw error;
      }
      // For network errors, re-throw with clean message
      if (error.name === "TypeError" && error.message === "Failed to fetch") {
        throw new Error("Network error - please check your connection");
      }
      throw error;
    }
  };

  // ============================================================
  // FIX 2: Handle auth errors correctly (no Axios dependencies)
  // ============================================================
  const handleAuthError = (error) => {
    // Check if it's an auth error from our fetch wrapper
    if (error.message?.includes("Authentication failed") ||
        error.message?.includes("redirecting to login")) {
      // Already redirected, just return true
      return true;
    }
    
    // Check for 401/403 in error message
    if (error.message?.includes("401") || error.message?.includes("403")) {
      localStorage.removeItem("token");
      window.location.href = "/login";
      return true;
    }
    
    return false;
  };

  // ============================================================
  // FIX 3: fetchUsers with fallback for missing backend aggregation
  // ============================================================
  const fetchUsers = useCallback(async (page = 1, search = searchQuery) => {
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      
      if (page < 1) {
        page = 1;
      }
      
      let url = `/users?page=${page}&limit=${itemsPerPage}`;
      
      if (search && search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      
      if (filterMode === "wallet") {
        url += `&register_form=false`;
      } else if (filterMode === "email") {
        url += `&register_form=true`;
      }

      // ============================================================
      // FIX: Request aggregated data - but handle gracefully if backend doesn't support it
      // ============================================================
      url += `&includeOrders=true&includeLocations=true`;

      const { data: userResponse } = await authFetch(url);

      const userData = userResponse.users || userResponse.data || [];
      const pagination = userResponse.pagination || {};

      setTotalPages(pagination.totalPages || 1);
      setTotalUsers(pagination.total || 0);
      setCurrentPage(page);

      // Check if data is already aggregated or needs client-side processing
      const processedUsers = userData.map((user) => {
        const displayName = user?.name || user?.wallet || "No Name Available";
        const displayEmail = user?.email || "No Email";

        // If backend already aggregated, use those values
        // Otherwise fallback to user's existing data
        return {
          ...user,
          name: displayName,
          email: displayEmail,
          tokens: user?.tokens || user?.amountWagr || "0",
          country: user?.country || "Unknown",
          city: user?.city || "Unknown",
          wallet: user?.wallet || "No Wallet",
        };
      });

      setUsers(processedUsers);
    } catch (error) {
      // ============================================================
      // FIX: Don't redirect for non-auth errors, just show toast
      // ============================================================
      if (!handleAuthError(error)) {
        console.log("Error fetching users:", error.message);
        setUsers([]);
        setTotalPages(1);
        setTotalUsers(0);
        toast.error(error.message || "Failed to fetch users. Please try again.");
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [searchQuery, filterMode, itemsPerPage]);

  // ============================================================
  // FIX 4: fetchUsersAndReferrals with graceful fallback
  // ============================================================
  const fetchUsersAndReferrals = useCallback(async () => {
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      
      let url = `/users?page=1&limit=${itemsPerPage}`;
      
      if (searchQuery && searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }
      
      if (filterMode === "wallet") {
        url += `&register_form=false`;
      } else if (filterMode === "email") {
        url += `&register_form=true`;
      }

      url += `&includeOrders=true&includeLocations=true&includeReferrals=true`;

      const { data: userResponse } = await authFetch(url);
      const userData = userResponse.users || userResponse.data || [];
      const pagination = userResponse.pagination || {};
      
      setTotalPages(pagination.totalPages || 1);
      setTotalUsers(pagination.total || 0);
      setCurrentPage(1);

      const processedUsers = userData.map((user) => {
        const displayName = user?.name || user?.wallet || "No Name Available";
        const displayEmail = user?.email || "No Email";

        return {
          ...user,
          name: displayName,
          email: displayEmail,
          tokens: user?.tokens || user?.amountWagr || "0",
          country: user?.country || "Unknown",
          city: user?.city || "Unknown",
          wallet: user?.wallet || "No Wallet",
        };
      });

      setUsers(processedUsers);

      if (userResponse.referrals) {
        setReferrals(userResponse.referrals);
      } else {
        const referralData = [];
        userData.forEach((user) => {
          if (user.referrals && user.referrals.length > 0) {
            user.referrals.forEach((ref) => {
              referralData.push({
                ...ref,
                referrer: user,
                referredUser: ref.referredUser || null,
              });
            });
          }
        });
        setReferrals(referralData);
      }
    } catch (error) {
      if (!handleAuthError(error)) {
        console.error("Error fetching users or referrals:", error.message);
        setUsers([]);
        setTotalPages(1);
        setTotalUsers(0);
        setReferrals([]);
        toast.error(error.message || "Failed to fetch data. Please try again.");
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [searchQuery, filterMode, itemsPerPage]);

  // Fetch data on component mount
  useEffect(() => {
    fetchUsersAndReferrals();
  }, []);

  // Handle search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const timeout = setTimeout(() => {
      if (searchQuery !== undefined) {
        fetchUsers(1, searchQuery);
      }
    }, 500);

    searchTimeoutRef.current = timeout;

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, filterMode, fetchUsers]);

  const currentItems = users;

  const handlePageChange = useCallback((pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) {
      return;
    }
    fetchUsers(pageNumber, searchQuery);
  }, [totalPages, searchQuery, fetchUsers]);

  const renderPagination = useCallback(() => {
    if (totalPages <= 1) return null;
    
    const pageNumbers = [];
    const maxPagesToShow = 3;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}
      >
        <Button
          color="primary"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
          style={{ margin: "0 5px", padding: "5px 10px" }}
        >
          Previous
        </Button>
        {pageNumbers.map((number) => (
          <Button
            key={number}
            color={currentPage === number ? "primary" : "secondary"}
            onClick={() => handlePageChange(number)}
            disabled={loading}
            style={{ margin: "0 5px", padding: "5px 10px" }}
          >
            {number}
          </Button>
        ))}
        <Button
          color="primary"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || loading}
          style={{ margin: "0 5px", padding: "5px 10px" }}
        >
          Next
        </Button>
      </div>
    );
  }, [currentPage, totalPages, loading, handlePageChange]);

  // Handle user edit
  const handleEditUser = (user) => {
    setSelectedUser({
      ...user,
      wallet: user.wallet,
    });
    toggleModal();
  };

  // ============================================================
  // FIX 5: handleDeleteUser - proper status check
  // ============================================================
  const handleDeleteUser = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const { data, status } = await authFetch(`/users/${id}`, {
          method: "DELETE",
        });
        
        if (status === 200) {
          toast.success(data.message || "User deleted successfully!");
          // ============================================================
          // FIX: Refresh users from server, not just local state
          // ============================================================
          await fetchUsers(currentPage, searchQuery);
        } else {
          toast.error(data.message || "Failed to delete user.");
        }
      } catch (error) {
        if (!handleAuthError(error)) {
          console.error("Error deleting user:", error.message);
          toast.error(error.message || "Failed to delete the user. Please try again.");
        }
      }
    }
  };

  // Handle "View All" button click
  const handleViewAllUser = (user) => {
    setSelectedUser(user);
    const referral = referrals.find(
      (ref) => ref.referredUser && ref.referredUser._id === user._id,
    );
    setSelectedReferral(referral || {});
    toggleViewAllModal();
  };

  // Toggle modals
  const toggleModal = () => setModal(!modal);
  const toggleTokenModal = () => setTokenModal(!tokenModal);
  const toggleViewAllModal = () => setViewAllModal(!viewAllModal);
  const toggleUserModal = () => setIsModalOpen(!isModalOpen);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUserData({ ...newUserData, [name]: value });
  };

  // ============================================================
  // FIX 6: handleSubmit - proper status check AND UI refresh
  // ============================================================
  const handleSubmit = async () => {
    try {
      newUserData.wallet = newUserData.wallet.toLowerCase();

      const { data: walletCheckData, status: walletCheckStatus } = await authFetch(
        `/check-wallet-address?wallet=${encodeURIComponent(newUserData.wallet)}`,
      );

      if (walletCheckStatus === 200 && walletCheckData.exists) {
        toast.warning("User with this wallet already exists!");
        return;
      }

      let password_hash = null;
      if (newUserData.password) {
        const { data: hashedData } = await authFetch(`/hash-password`, {
          method: "POST",
          body: JSON.stringify({ password: newUserData.password }),
        });
        password_hash = hashedData.hash;
      }

      const userData = {
        ...newUserData,
        password_hash,
        wallet: newUserData.wallet,
      };

      if (!userData.email) userData.email = null;
      if (!userData.name) userData.name = null;

      const { data, status } = await authFetch("/users", {
        method: "POST",
        body: JSON.stringify(userData),
      });

      if (status === 200 || status === 201) {
        toast.success("User created successfully!");
        toggleUserModal();
        // ============================================================
        // FIX: Refresh users from server to show new user in table
        // ============================================================
        await fetchUsers(currentPage, searchQuery);
        // Also update userList state if needed elsewhere
        setUserList(prev => [...prev, data.user || data]);
      } else {
        toast.error(data.message || "Failed to create user.");
      }
    } catch (error) {
      // ============================================================
      // FIX: Proper error handling for 409 conflicts
      // ============================================================
      if (error?.status === 409) {
        toast.warning(error?.data?.message || "User with this email or wallet already exists");
      } else if (!handleAuthError(error)) {
        let errorMsg = "Error creating user.";
        if (error.message && error.message.includes("status: 409")) {
          errorMsg = "User with this email or wallet already exists";
          toast.warning(errorMsg);
        } else if (error?.data?.message) {
          errorMsg = error.data.message;
          toast.error(errorMsg);
        } else {
          toast.error(errorMsg);
        }
        console.error("Error creating user:", error);
      }
    }
  };

  // Handle search query change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle filter mode change
  const handleRegisterWithWallet = () => {
    setFilterMode("wallet");
    setCurrentPage(1);
  };
  
  const handleRegisterWithEmail = () => {
    setFilterMode("email");
    setCurrentPage(1);
  };
  
  const handleRegister = () => {
    setFilterMode("");
    setCurrentPage(1);
  };

  // Check if login is recent
  const isRecentLogin = (lastLogin) => {
    if (!lastLogin) return false;
    const currentDate = new Date();
    const loginDate = new Date(lastLogin);
    return currentDate.toDateString() === loginDate.toDateString();
  };

  // ============================================================
  // FIX 7: handleEditToken - uses already aggregated user data
  // ============================================================
  const handleEditToken = async (user) => {
    try {
      if (user.tokens !== undefined && user.tokens !== null) {
        setSelectedUser({
          ...user,
          tokens: user.tokens,
        });
        toggleTokenModal();
      } else {
        toast.info("No token data available for this user");
        console.log("No token found for user:", user._id);
      }
    } catch (error) {
      if (!handleAuthError(error)) {
        console.error("Error fetching tokens:", error);
        toast.error("Failed to fetch tokens.");
      }
    }
  };

  // ============================================================
  // FIX 8: handleUpdateToken - proper status check
  // ============================================================
  const handleUpdateToken = async () => {
    try {
      const { data, status } = await authFetch(`/orders/${selectedUser._id}`, {
        method: "PUT",
        body: JSON.stringify({
          amountWagr: selectedUser.tokens,
        }),
      });

      if (status === 200) {
        toast.success("Token Updated successfully");
        await fetchUsers(currentPage, searchQuery);
        toggleTokenModal();
      } else {
        toast.error(data.message || "Failed to update tokens.");
      }
    } catch (error) {
      if (!handleAuthError(error)) {
        toast.error("Error updating tokens.");
        console.error(error);
        toggleTokenModal();
      }
    }
  };

  // ============================================================
  // FIX 9: handleUpdateUser - proper status check
  // ============================================================
  const handleUpdateUser = async () => {
    try {
      let password_hash = selectedUser.password_hash;

      if (selectedUser.password) {
        password_hash = selectedUser.password;
      }

      const { data, status } = await authFetch(`/users/${selectedUser._id}`, {
        method: "PUT",
        body: JSON.stringify({
          name: selectedUser.name,
          phoneNumber: selectedUser.phoneNumber,
          role: selectedUser.role,
          wallet: selectedUser.wallet,
          passwordHash: password_hash,
          city: selectedUser.city,
          country: selectedUser.country,
          isblocked: selectedUser.isblocked,
          profile_completed: selectedUser.profile_completed,
        }),
      });

      if (status === 200) {
        toast.success("User updated successfully");
        await fetchUsers(currentPage, searchQuery);
        toggleModal();
      } else {
        toast.error(data.message || "Failed to update user.");
      }
    } catch (error) {
      if (!handleAuthError(error)) {
        if (error.response && error.response.status === 400) {
          toast.warning(error.response.data.error);
        } else {
          toast.error(error.message || "Error updating user.");
        }
        toggleModal();
      }
    }
  };

  const handleregister = () => {
    setFilterMode("");
    setCurrentPage(1);
  };

  return (
    <>
      <Modal isOpen={isModalOpen} toggle={toggleUserModal}>
        <ModalHeader toggle={toggleUserModal}>Insert New User</ModalHeader>
        <ModalBody>
          <label style={{ color: "black" }}>Full Name</label>
          <Input
            type="text"
            name="name"
            placeholder="Full Name"
            value={newUserData.name}
            onChange={handleInputChange}
          />
          <br />
          <label style={{ color: "black" }}>Email</label>
          <Input
            type="email"
            name="email"
            placeholder="Email"
            value={newUserData.email}
            onChange={handleInputChange}
          />
          <br />
          <label style={{ color: "black" }}>Phone Number</label>
          <Input
            type="text"
            name="phoneNumber"
            placeholder="Phone Number"
            value={newUserData.phoneNumber}
            onChange={handleInputChange}
          />
          <br />
          <label style={{ color: "black" }}>Password</label>
          <Input
            type="password"
            name="password"
            placeholder="Password"
            value={newUserData.password}
            onChange={handleInputChange}
          />
          <br />
          <label style={{ color: "black" }}>Wallet Address</label>
          <Input
            type="text"
            name="wallet"
            placeholder="Wallet Address"
            value={newUserData.wallet}
            onChange={handleInputChange}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSubmit}>
            Submit
          </Button>
          <Button color="secondary" onClick={toggleUserModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <div
        className="container"
        style={{ border: "none", paddingTop: "100px", maxWidth: "100%" }}
      >
        <Row
          className=""
          style={{
            border: "none",
            backgroundColor: "rgba(1,1,1,0.2)",
            marginTop: "20px",
          }}
        >
          <div className="col">
            <Card
              className=""
              style={{ border: "none", backgroundColor: "transparent" }}
            >
              <Row
                style={{ marginBottom: "20px" }}
                className="align-items-center"
              >
                <Col xs="12" sm="8" md="9">
                  <Input
                    type="text"
                    value={searchQuery}
                    style={{
                      background: "black",
                      border: "none",
                      outline: "none",
                      padding: "15px",
                      fontSize: "16px",
                      color: "white",
                    }}
                    onChange={handleSearchChange}
                    placeholder="Search by Full Name, Email or Wallet address"
                  />
                </Col>

                <Col
                  xs="12"
                  sm="4"
                  md="3"
                  className="d-flex flex-column align-items-center"
                >
                  <Button
                    color="primary"
                    onClick={toggleUserModal}
                    className="ml-auto w-100 mb-2"
                    style={{ fontSize: "16px" }}
                  >
                    <FaPlusCircle style={{ marginRight: "8px" }} /> Insert User
                  </Button>
                </Col>
              </Row>

              <CardHeader
                className="border-0"
                style={{
                  backgroundColor: "black",
                  border: "none",
                  paddingLeft: "20px",
                }}
              >
                <Row
                  style={{ marginBottom: "20px" }}
                  className="align-items-center"
                >
                  <Col xs="12" sm="8" md="5">
                    <h3
                      className="mb-0"
                      style={{
                        color: "white",
                        fontSize: "40px",
                        fontWeight: "bolder",
                      }}
                    >
                      Users List
                    </h3>
                    {totalUsers > 0 && (
                      <p style={{ color: "gray", fontSize: "14px" }}>
                        Total: {totalUsers} users
                      </p>
                    )}
                  </Col>
                  <br />
                  <br />
                  <br />
                  <Col
                    xs="12"
                    sm="4"
                    md="1"
                    className="d-flex flex-column align-items-center"
                  >
                    <Button
                      onClick={handleregister}
                      style={{ fontSize: "16px" }}
                    >
                      All
                    </Button>
                  </Col>
                  <br />
                  <Col
                    xs="12"
                    sm="4"
                    md="3"
                    className="d-flex flex-column align-items-center"
                  >
                    <Button
                      onClick={handleRegisterWithWallet}
                      style={{ fontSize: "16px" }}
                    >
                      <FaWallet style={{ marginRight: "8px" }} /> Register with
                      Wallet
                    </Button>
                  </Col>
                  <br />
                  <br />
                  <br />
                  <Col
                    xs="12"
                    sm="4"
                    md="3"
                    className="d-flex flex-column align-items-center"
                  >
                    <Button
                      onClick={handleRegisterWithEmail}
                      style={{ fontSize: "16px" }}
                    >
                      <FaEnvelope style={{ marginRight: "8px" }} /> Register
                      with Email
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              {loading ? (
                <div style={{ textAlign: "center", marginTop: "20px" }}>
                  <Spinner color="primary" />
                  <p style={{ color: "white", marginTop: "10px" }}>Loading...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        {filterMode === "wallet" ? (
                          <>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Wallet Address
                            </th>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Tokens
                            </th>
                          </>
                        ) : filterMode === "email" ? (
                          <>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Name
                            </th>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Email
                            </th>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Tokens
                            </th>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Phone Number
                            </th>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Last Login
                            </th>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Country
                            </th>
                          </>
                        ) : (
                          <>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Name
                            </th>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Email
                            </th>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Phone Number
                            </th>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Token
                            </th>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Last Login
                            </th>
                            <th
                              style={{
                                fontSize: "13px",
                                color: "white",
                                border: "none",
                                background: "black",
                                padding: "10px",
                              }}
                            >
                              Country
                            </th>
                          </>
                        )}
                        <th
                          style={{
                            fontSize: "13px",
                            color: "white",
                            border: "none",
                            background: "black",
                            padding: "10px",
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length === 0 ? (
                        <tr>
                          <td colSpan="7" style={{ color: "white", textAlign: "center", padding: "20px" }}>
                            No users found
                          </td>
                        </tr>
                      ) : (
                        currentItems.map((user) => (
                          <tr key={user._id} style={{ border: "none" }}>
                            {filterMode === "wallet" ? (
                              <>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px",
                                  }}
                                >
                                  {user.wallet || "Null"}
                                </td>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px",
                                  }}
                                >
                                  {user.tokens
                                    ? Number(user.tokens).toLocaleString()
                                    : "0"}
                                </td>
                              </>
                            ) : filterMode === "email" ? (
                              <>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px",
                                  }}
                                >
                                  {user.name || "Null"}
                                </td>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px",
                                  }}
                                >
                                  {user.email || "Null"}
                                </td>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px",
                                  }}
                                >
                                  {user.tokens
                                    ? Number(user.tokens).toLocaleString()
                                    : "0"}
                                </td>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px",
                                  }}
                                >
                                  {user.phoneNumber || "Null"}
                                </td>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px 5px",
                                    fontSize: "0.9em",
                                    height: "0.1vh",
                                    paddingLeft: "20px",
                                  }}
                                >
                                  {user.last_login || "Never"}
                                  <span>
                                    {isRecentLogin(user.last_login) ? (
                                      <FaCheckCircle
                                        style={{
                                          color: "green",
                                          marginLeft: "10px",
                                        }}
                                      />
                                    ) : (
                                      <FaTimesCircle
                                        style={{ color: "red", marginLeft: "10px" }}
                                      />
                                    )}
                                  </span>
                                </td>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px",
                                  }}
                                >
                                  {user.country || "Unknown"}
                                </td>
                              </>
                            ) : (
                              <>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px",
                                  }}
                                >
                                  {user.name || "Null"}
                                </td>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px",
                                  }}
                                >
                                  {user.email || "Null"}
                                </td>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px",
                                  }}
                                >
                                  {user.phoneNumber || "Null"}
                                </td>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px",
                                  }}
                                >
                                  {user.totalWagr
                                    ? Number(user.totalWagr).toLocaleString()
                                    : "0"}
                                </td>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px 5px",
                                    fontSize: "0.9em",
                                    height: "0.1vh",
                                    paddingLeft: "20px",
                                  }}
                                >
                                  {user.last_login || "Never"}
                                  <span>
                                    {isRecentLogin(user.last_login) ? (
                                      <FaCheckCircle
                                        style={{
                                          color: "green",
                                          marginLeft: "10px",
                                        }}
                                      />
                                    ) : (
                                      <FaTimesCircle
                                        style={{ color: "red", marginLeft: "10px" }}
                                      />
                                    )}
                                  </span>
                                </td>
                                <td
                                  style={{
                                    color: "white",
                                    border: "none",
                                    background: "black",
                                    padding: "10px",
                                  }}
                                >
                                  {user.country || "Unknown"}
                                </td>
                              </>
                            )}
                            <td
                              style={{
                                color: "white",
                                border: "none",
                                background: "black",
                              }}
                            >
                              <Button
                                style={{
                                  fontSize: "0.6em",
                                  padding: "10px",
                                  margin: "2px",
                                }}
                                color="dark"
                                onClick={() => handleEditUser(user)}
                              >
                                Edit
                              </Button>
                              <Button
                                style={{
                                  fontSize: "0.6em",
                                  padding: "10px",
                                  margin: "2px",
                                }}
                                color="info"
                                onClick={() => handleViewAllUser(user)}
                              >
                                View All
                              </Button>
                              {/* <Button
                                style={{
                                  fontSize: "0.6em",
                                  padding: "10px",
                                  margin: "2px",
                                }}
                                color="warning"
                                onClick={() => handleEditToken(user)}
                              >
                                Edit Token
                              </Button> */}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              {renderPagination()}
              <br />
            </Card>
          </div>
        </Row>

        <Modal isOpen={modal} toggle={toggleModal}>
          <ModalHeader toggle={toggleModal}>Edit User</ModalHeader>
          <ModalBody>
            <label style={{ color: "black" }}>Full Name</label>
            <Input
              type="text"
              value={selectedUser.name}
              onChange={(e) =>
                setSelectedUser({ ...selectedUser, name: e.target.value })
              }
              placeholder="Full Name"
            />
            <label style={{ color: "black" }}>Email</label>
            <Input
              type="email"
              value={selectedUser.email}
              onChange={(e) =>
                setSelectedUser({ ...selectedUser, email: e.target.value })
              }
              placeholder="Email"
            />
            <label style={{ color: "black" }}>Phone Number</label>
            <Input
              type="text"
              value={selectedUser.phoneNumber}
              onChange={(e) =>
                setSelectedUser({
                  ...selectedUser,
                  phoneNumber: e.target.value,
                })
              }
              placeholder="Phone Number"
            />
            <label style={{ color: "black" }}>Role</label>
            <Input
              type="select"
              value={selectedUser.role}
              onChange={(e) =>
                setSelectedUser({ ...selectedUser, role: e.target.value })
              }
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </Input>
            <label style={{ color: "black" }}>Wallet Address</label>
            <Input
              type="text"
              value={selectedUser.wallet}
              onChange={(e) =>
                setSelectedUser({ ...selectedUser, wallet: e.target.value })
              }
              placeholder="Wallet Address"
            />
            <label style={{ color: "black" }}>Reset Password</label>
            <Input
              type="text"
              value={selectedUser.password_hash}
              onChange={(e) =>
                setSelectedUser({
                  ...selectedUser,
                  password_hash: e.target.value,
                })
              }
              placeholder="Reset User Password Or Enter new password"
            />
            <label style={{ color: "black" }}>
              <Input
                type="checkbox"
                checked={selectedUser.isblocked}
                onChange={(e) =>
                  setSelectedUser({
                    ...selectedUser,
                    isblocked: e.target.checked,
                  })
                }
              />
              Block User
            </label>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={handleUpdateUser}>
              Save Changes
            </Button>
            <Button color="secondary" onClick={toggleModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={tokenModal} toggle={toggleTokenModal}>
          <ModalHeader toggle={toggleTokenModal}>
            Edit Tokens for {selectedUser.name}
          </ModalHeader>
          <ModalBody>
            <div>
              <label htmlFor="tokens" style={{ color: "black" }}>
                Tokens
              </label>
              <Input
                type="number"
                id="tokens"
                value={selectedUser.tokens || ""}
                onChange={(e) =>
                  setSelectedUser({ ...selectedUser, tokens: e.target.value })
                }
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={handleUpdateToken}>
              Save
            </Button>
            <Button color="secondary" onClick={toggleTokenModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={viewAllModal} toggle={toggleViewAllModal} size="lg">
          <ModalHeader toggle={toggleViewAllModal}>
            User Details - {selectedUser.name}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md="6">
                <p style={{ color: "black" }}>
                  <strong>ID:</strong> {selectedUser._id || "Not available"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Full Name:</strong>{" "}
                  {selectedUser.name || "Not available"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Email:</strong>{" "}
                  {selectedUser.email || "Not available"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Phone Number:</strong>{" "}
                  {selectedUser.phoneNumber || "Not available"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Role:</strong> {selectedUser.role || "Not available"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Wallet Address:</strong>{" "}
                  {selectedUser.wallet || "Not available"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>City:</strong> {selectedUser.city || "Unknown"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Country:</strong> {selectedUser.country || "Unknown"}
                </p>
              </Col>
              <Col md="6">
                <p style={{ color: "black" }}>
                  <strong>Tokens:</strong> {selectedUser.tokens || "0"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Total Deposit:</strong>{" "}
                  {selectedUser.totalDeposit || "0"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Total Wagr:</strong> {selectedUser.totalWagr || "0"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Referral Friends:</strong>{" "}
                  {selectedUser.totalReferralFriends || "0"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Two-Way Authentication:</strong>{" "}
                  {selectedUser.two_way_auth ? "Enabled" : "Disabled"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Profile Completed:</strong>{" "}
                  {selectedUser.profile_completed ? "Yes" : "No"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Notification Signup:</strong>{" "}
                  {selectedUser.notification_signup ? "Yes" : "No"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Blocked:</strong>{" "}
                  {selectedUser.isblocked ? "Yes" : "No"}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Kicked:</strong> {selectedUser.kicked ? "Yes" : "No"}
                </p>
              </Col>
            </Row>
            {selectedReferral.referrer_id && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "15px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "5px",
                }}
              >
                <h5 style={{ color: "black" }}>Referral Information</h5>
                <p style={{ color: "black" }}>
                  <strong>Referrer ID:</strong> {selectedReferral.referrer_id}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Referred User ID:</strong>{" "}
                  {selectedReferral.referred_user_id}
                </p>
                {selectedReferral.referrer && (
                  <p style={{ color: "black" }}>
                    <strong>Referrer Name:</strong>{" "}
                    {selectedReferral.referrer.name}
                  </p>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleViewAllModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        <ToastContainer position="top-center" />
      </div>
    </>
  );
};

export default Userlist;